import React from "react";
import { useTranslation } from "react-i18next";
import { ArrivingIcon, ChevronRightIcon, DepartingIcon } from "./icons";
import { FlightDirection } from "./types";

export interface DirectionStepProps {
  onSelect: (direction: FlightDirection) => void;
}

export const DirectionStep: React.FC<DirectionStepProps> = ({ onSelect }) => {
  const { t } = useTranslation("flightAlerts");

  const cards: Array<{
    direction: FlightDirection;
    Icon: typeof ArrivingIcon;
    iconColor: string;
    title: string;
    subtitle: string;
  }> = [
    {
      direction: "arrival",
      Icon: ArrivingIcon,
      iconColor: "text-ink-100",
      title: t("direction.arrivingTitle"),
      subtitle: t("direction.arrivingSubtitle"),
    },
    {
      direction: "departure",
      Icon: DepartingIcon,
      iconColor: "text-brand-blue",
      title: t("direction.departingTitle"),
      subtitle: t("direction.departingSubtitle"),
    },
  ];

  return (
    <>
      {/* Figma spec: Lexend Deca, Bold 700, 28px/32px, -1px letter-spacing,
          ink/100 (#0F151A) — same on mobile and desktop (DASH-6077). */}
      <h1 className="font-lexend text-[28px]/[32px] font-bold tracking-[-1px] text-ink-100">
        {t("direction.heading")}
      </h1>
      {/* Figma spec: gap 12px mobile (cards stacked) / 24px desktop (cards
          side by side) (DASH-6077). */}
      <div className="flex flex-col md:flex-row gap-3 md:gap-6">
        {cards.map(({ direction, Icon, iconColor, title, subtitle }) => (
          <button
            key={direction}
            type="button"
            onClick={() => onSelect(direction)}
            className="flex-1 flex items-center md:flex-col md:items-start gap-4 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8 text-left hover:border-brand-blue transition-colors"
          >
            {/* Figma spec: 56x56 circle, bg/app background; glyph ~28px,
                ink/100 for arriving, primary-100/brand-blue for departing
                (DASH-6077). */}
            <span className="flex-shrink-0 w-14 h-14 rounded-full bg-app flex items-center justify-center">
              <Icon className={`h-7 w-auto ${iconColor}`} />
            </span>
            <span className="flex-1 flex flex-col gap-1">
              {/* Figma spec: Lexend Deca, Bold 700, 20px/24px, -0.4px
                  letter-spacing, ink/100 (DASH-6077). */}
              <span className="font-lexend text-[20px]/[24px] font-bold tracking-[-0.4px] text-ink-100">
                {title}
              </span>
              {/* Figma spec: Lexend Deca, Regular 400, 14px/18px, 0
                  letter-spacing, ink/60 (DASH-6077). */}
              <span className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
                {subtitle}
              </span>
            </span>
            {/* Figma spec: mobile-only, 24px, ink/40 (not present in the
                desktop card) (DASH-6077). */}
            <ChevronRightIcon className="w-6 h-6 text-ink-40 flex-shrink-0 md:hidden" />
          </button>
        ))}
      </div>
    </>
  );
};
